import { createStore } from "zustand/vanilla";
import {
  ReadProjectStorePeriod,
  StoreInPeriod,
  ClientStoreNumber,
} from "@/types/projectStorePeriod";

interface StoreEntry {
  clientStoreNumber: string;
  periods: Record<string, boolean>;
}

type EditedPeriods = Record<string, Record<string, boolean>>;
type EditedClientStoreNumbers = Record<string, string>;

export interface ProjectStoreFormState {
  // Server baseline — replaced wholesale on sync
  serverStores: Record<string, StoreEntry>;

  // Sparse user edits (only what the user actually changed)
  editedPeriods: EditedPeriods;
  editedClientStoreNumbers: EditedClientStoreNumbers;

  // UI state
  isDirty: boolean;
  isSubmitting: boolean;
  removeStoresWithoutPeriods: boolean;
}

export interface ProjectStoreFormActions {
  togglePeriod: (storeId: string, periodId: string) => void;
  setClientStoreNumber: (storeId: string, value: string) => void;
  syncFromServer: (data: ReadProjectStorePeriod[]) => void;
  bulkSetPeriods: (
    storeIds: string[],
    periodIds: string[],
    active: boolean,
  ) => void;
  clearAndRemoveStore: (storeId: string, periodIds: string[]) => void;
  setRemoveStoresWithoutPeriods: (value: boolean) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  reset: () => void;
  getChanges: () => {
    storesInPeriods: StoreInPeriod[];
    clientStoreNumbers: ClientStoreNumber[];
  };
  getStoresWithoutPeriods: () => string[];
}

export type ProjectStoreFormStore = ProjectStoreFormState &
  ProjectStoreFormActions;

export function selectPeriod(
  state: ProjectStoreFormState,
  storeId: string,
  periodId: string,
): boolean {
  return (
    state.editedPeriods[storeId]?.[periodId] ??
    state.serverStores[storeId]?.periods[periodId] ??
    false
  );
}

export function selectClientStoreNumber(
  state: ProjectStoreFormState,
  storeId: string,
): string {
  return (
    state.editedClientStoreNumbers[storeId] ??
    state.serverStores[storeId]?.clientStoreNumber ??
    ""
  );
}

function computeIsDirty(
  editedPeriods: EditedPeriods,
  editedClientStoreNumbers: EditedClientStoreNumbers,
  serverStores: Record<string, StoreEntry>,
): boolean {
  for (const [storeId, periods] of Object.entries(editedPeriods)) {
    for (const [periodId, value] of Object.entries(periods)) {
      if (value !== (serverStores[storeId]?.periods[periodId] ?? false)) {
        return true;
      }
    }
  }
  for (const [storeId, value] of Object.entries(editedClientStoreNumbers)) {
    if (value !== (serverStores[storeId]?.clientStoreNumber ?? "")) {
      return true;
    }
  }
  return false;
}

function buildServerStores(
  data: ReadProjectStorePeriod[],
): Record<string, StoreEntry> {
  const serverStores: Record<string, StoreEntry> = {};
  for (const item of data) {
    const periods: Record<string, boolean> = {};
    for (const period of item.periods) {
      periods[period.id] = period.store_active;
    }
    serverStores[item.id] = {
      clientStoreNumber: item.client_store_number ?? "",
      periods,
    };
  }
  return serverStores;
}

export function createProjectStoreFormStore() {
  return createStore<ProjectStoreFormStore>((set, get) => ({
    // State
    serverStores: {},
    editedPeriods: {},
    editedClientStoreNumbers: {},
    isDirty: false,
    isSubmitting: false,
    removeStoresWithoutPeriods: false,

    // Actions
    togglePeriod: (storeId, periodId) => {
      set((state) => {
        const serverValue =
          state.serverStores[storeId]?.periods[periodId] ?? false;
        const currentValue =
          state.editedPeriods[storeId]?.[periodId] ?? serverValue;
        const newEditedPeriods = {
          ...state.editedPeriods,
          [storeId]: {
            ...state.editedPeriods[storeId],
            [periodId]: !currentValue,
          },
        };
        return {
          editedPeriods: newEditedPeriods,
          isDirty: computeIsDirty(
            newEditedPeriods,
            state.editedClientStoreNumbers,
            state.serverStores,
          ),
        };
      });
    },

    setClientStoreNumber: (storeId, value) => {
      set((state) => {
        const newEditedClientStoreNumbers = {
          ...state.editedClientStoreNumbers,
          [storeId]: value,
        };
        return {
          editedClientStoreNumbers: newEditedClientStoreNumbers,
          isDirty: computeIsDirty(
            state.editedPeriods,
            newEditedClientStoreNumbers,
            state.serverStores,
          ),
        };
      });
    },

    syncFromServer: (data) => {
      const newServerStores = buildServerStores(data);
      set((state) => ({
        serverStores: newServerStores,
        isDirty: computeIsDirty(
          state.editedPeriods,
          state.editedClientStoreNumbers,
          newServerStores,
        ),
      }));
    },

    bulkSetPeriods: (storeIds, periodIds, active) => {
      set((state) => {
        const newEditedPeriods = { ...state.editedPeriods };
        for (const storeId of storeIds) {
          newEditedPeriods[storeId] = { ...newEditedPeriods[storeId] };
          for (const periodId of periodIds) {
            newEditedPeriods[storeId][periodId] = active;
          }
        }
        return {
          editedPeriods: newEditedPeriods,
          isDirty: computeIsDirty(
            newEditedPeriods,
            state.editedClientStoreNumbers,
            state.serverStores,
          ),
        };
      });
    },

    clearAndRemoveStore: (storeId, periodIds) => {
      set((state) => {
        const clearedPeriods: Record<string, boolean> = {};
        for (const periodId of periodIds) {
          clearedPeriods[periodId] = false;
        }
        const newEditedPeriods = {
          ...state.editedPeriods,
          [storeId]: {
            ...state.editedPeriods[storeId],
            ...clearedPeriods,
          },
        };
        return {
          editedPeriods: newEditedPeriods,
          removeStoresWithoutPeriods: true,
          isDirty: computeIsDirty(
            newEditedPeriods,
            state.editedClientStoreNumbers,
            state.serverStores,
          ),
        };
      });
    },

    setRemoveStoresWithoutPeriods: (value) => {
      set({ removeStoresWithoutPeriods: value });
    },

    setIsSubmitting: (isSubmitting) => {
      set({ isSubmitting });
    },

    reset: () => {
      set({
        editedPeriods: {},
        editedClientStoreNumbers: {},
        isDirty: false,
        removeStoresWithoutPeriods: false,
      });
    },

    getChanges: () => {
      const state = get();
      const periodMap = new Map<string, { add: string[]; delete: string[] }>();

      for (const [storeId, periods] of Object.entries(state.editedPeriods)) {
        for (const [periodId, value] of Object.entries(periods)) {
          const serverValue =
            state.serverStores[storeId]?.periods[periodId] ?? false;
          if (value !== serverValue) {
            const entry = periodMap.get(periodId) ?? {
              add: [],
              delete: [],
            };
            (value ? entry.add : entry.delete).push(storeId);
            periodMap.set(periodId, entry);
          }
        }
      }

      return {
        storesInPeriods: Array.from(periodMap.entries()).map(
          ([periodId, { add, delete: del }]) => ({
            period_id: periodId,
            add,
            delete: del,
          }),
        ),
        clientStoreNumbers: Object.entries(state.editedClientStoreNumbers)
          .filter(
            ([storeId, value]) =>
              value !== (state.serverStores[storeId]?.clientStoreNumber ?? ""),
          )
          .map(([storeId, value]) => ({
            store_id: storeId,
            store_number: value,
          })),
      };
    },

    getStoresWithoutPeriods: () => {
      const state = get();
      return Object.keys(state.serverStores).filter((storeId) => {
        const serverPeriods = state.serverStores[storeId].periods;
        const edits = state.editedPeriods[storeId];
        return Object.entries(serverPeriods).every(
          ([periodId, serverValue]) => {
            const value = edits?.[periodId] ?? serverValue;
            return !value;
          },
        );
      });
    },
  }));
}

export type ProjectStoreFormStoreApi = ReturnType<
  typeof createProjectStoreFormStore
>;
